import {
  defaultTheme,
  MenuPlacement,
  MenuPosition,
  components,
} from 'react-select'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'

/** Picks #1f1e1e or #ffffff for readable text on a solid hex background. */
export function foregroundForPrimaryBackground(primary: unknown): string {
  if (typeof primary !== 'string' || !/^#[0-9A-Fa-f]{6}$/i.test(primary)) {
    return '#1f1e1e'
  }
  const hex = primary.slice(1)
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const linearize = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const luminance =
    0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)
  return luminance > 0.45 ? '#1f1e1e' : '#ffffff'
}

export type ReactSelectAccentColors = {
  primary25: string
  primary50: string
  primary75: string
  primary: string
}

/**
 * Default accent palette (lib-front / skop-manage amber). Consumers pass
 * `theme={reactSelectTheme}` so these merge into react-select’s theme; styles
 * read merged colors from `state.theme` so overrides actually apply.
 */
export const reactSelectColors: ReactSelectAccentColors = {
  primary25: '#ffe7b3',
  primary50: '#fcd37e',
  primary75: '#ffcf6b',
  primary: '#ffc857',
}

const getReactSelectAccentColors = (theme: {
  colors?: Record<string, string | undefined>
}): ReactSelectAccentColors => {
  const c = theme?.colors
  if (!c) return reactSelectColors
  return {
    primary25: c.primary25 ?? reactSelectColors.primary25,
    primary50: c.primary50 ?? reactSelectColors.primary50,
    primary75: c.primary75 ?? reactSelectColors.primary75,
    primary: c.primary ?? reactSelectColors.primary,
  }
}

export const reactSelectTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    ...reactSelectColors,
  },
})

export type ResolvedSelectAccentColors = ReactSelectAccentColors & {
  onPrimary: string
}

export const resolveReactSelectAccentColors = (
  themeFn?: ((theme: typeof defaultTheme) => typeof defaultTheme) | null,
): ResolvedSelectAccentColors => {
  const merged = (themeFn ?? reactSelectTheme)(defaultTheme)
  const accent = getReactSelectAccentColors(merged)
  return {
    ...accent,
    onPrimary: foregroundForPrimaryBackground(accent.primary),
  }
}

export const reactSelectStyles = {
  menuPortal: (base: object) => ({
    ...base,
    zIndex: 9999,
    pointerEvents: 'auto' as const,
  }),
  control: (styles: object, state: { isFocused: boolean; theme: any }) => {
    const { primary } = getReactSelectAccentColors(state.theme)
    return {
      ...styles,
      fontSize: '14px',
      minHeight: '41px',
      borderRadius: '4px',
      border: '1px solid rgb(235,234,239)',
      '&:hover': {
        border: '1px solid rgb(235,234,239)',
      },
      cursor: 'pointer',
      boxShadow: state.isFocused
        ? `0 0 0 2px #fff, 0 0 0 3px ${primary}`
        : 'none',
      '*': {
        boxShadow: 'none !important',
      },
    }
  },

  option: (styles: Record<string, unknown>, state: any) => {
    const { isSelected, isFocused, isDisabled, theme } = state
    const { primary, primary25, primary50 } = getReactSelectAccentColors(theme)

    if (isDisabled) {
      return {
        ...styles,
        fontSize: '14px',
      }
    }

    return {
      ...styles,
      backgroundColor: isSelected
        ? primary
        : isFocused
          ? primary25
          : 'transparent',
      color: isSelected
        ? foregroundForPrimaryBackground(primary)
        : isFocused
          ? '#1f1e1e'
          : '#343232',
      fontSize: '14px',
      ':active': {
        ...(styles[':active'] as object | undefined),
        backgroundColor: isSelected ? primary : primary50,
      },
    }
  },
  multiValueLabel: (styles: object) => ({
    ...styles,
    fontSize: '14px',
  }),
  menu: (styles: object) => ({
    ...styles,
    left: 0,
  }),
  placeholder: (styles: object, state: any) => ({
    ...styles,
    fontWeight: state.selectProps['aria-required'] ? 'medium' : 'normal',
    ':after': {
      content: state.selectProps['aria-required'] ? "'*'" : null,
      marginLeft: '0.25rem',
    },
  }),
}

export const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
}

let portalTarget: HTMLElement | null = null

const getPortalTarget = () => {
  if (typeof document === 'undefined') return undefined
  if (!portalTarget) {
    const element = document.createElement('div')
    element.setAttribute('data-radix-scroll-lock-ignore', '')
    element.style.position = 'absolute'
    element.style.top = '0'
    element.style.left = '0'
    document.body.appendChild(element)
    portalTarget = element
  }
  return portalTarget
}

export const overflowProps = (overflow: boolean) => {
  return overflow
    ? {
        menuPlacement: 'auto' as MenuPlacement,
        menuPosition: 'fixed' as MenuPosition,
        menuShouldScrollIntoView: false,
        menuPortalTarget: getPortalTarget(),
      }
    : {}
}

export const ClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <div className='cursor-pointer'>
        <XIcon className='size-5' />
      </div>
    </components.ClearIndicator>
  )
}

export const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <div className='cursor-pointer'>
        <CaretDownIcon className='size-5' />
      </div>
    </components.DropdownIndicator>
  )
}

export const MenuList = (props: any) => {
  return (
    <div
      onWheel={(e) => {
        e.stopPropagation()
      }}
      onTouchMove={(e) => {
        e.stopPropagation()
      }}
    >
      <components.MenuList {...props} />
    </div>
  )
}
